import {
	type ColumnDef,
	type ColumnFiltersState,
	type SortingState,
	type VisibilityState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/modules/shared/components/ui/button'
import { Input } from '@/modules/shared/components/ui/input'
import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
} from '@/modules/shared/components/ui/table'

import type { SellerReportType } from '../../types/seller-report.type'

function SortingIcon({ direction }: { direction: false | 'asc' | 'desc' }) {
	const Icon =
		direction === 'asc' ? ArrowUp : direction === 'desc' ? ArrowDown : ArrowUpDown

	return (
		<Icon
			className={`ml-2 h-4 w-4 ${direction ? 'text-primary' : 'text-muted-foreground'}`}
			aria-label={
				direction === 'asc'
					? 'Ordem crescente'
					: direction === 'desc'
						? 'Ordem decrescente'
						: 'Sem ordenação'
			}
		/>
	)
}

export const columns: ColumnDef<SellerReportType>[] = [
	{
		accessorKey: 'razaoSocialNome',
		header: ({ column }) => (
			<Button
				size="sm"
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
			>
				REPRESENTANTE
				<SortingIcon direction={column.getIsSorted()} />
			</Button>
		),
	},
	{
		accessorKey: 'anual',
		header: ({ column }) => {
			return (
				<Button
					size="sm"
					className="text-wrap"
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					COTA ANUAL
					<SortingIcon direction={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const amount = Number.parseFloat(row.getValue('anual'))
			const formatted = new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			})
				.format(amount)
				.replace('R$', '')

			return <div>{formatted}</div>
		},
		footer: ({ table }) => {
			const total = table
				.getFilteredRowModel()
				.rows.reduce(
					(total, row) => total + Number.parseFloat(row.getValue('anual')),
					0,
				)

			const formatted = new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			})
				.format(total)
				.replace('R$', '')
			return <div>{formatted}</div>
		},
	},
	{
		accessorKey: 'trimestral',
		header: ({ column }) => {
			return (
				<Button
					size="sm"
					className="text-wrap"
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					COTA TRIMESTRAL
					<SortingIcon direction={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const amount = Number.parseFloat(row.getValue('trimestral'))
			const formatted = new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			})
				.format(amount)
				.replace('R$', '')

			return <div>{formatted}</div>
		},
		footer: ({ table }) => {
			const total = table
				.getFilteredRowModel()
				.rows.reduce(
					(total, row) => total + Number.parseFloat(row.getValue('trimestral')),
					0,
				)

			const formatted = new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			})
				.format(total)
				.replace('R$', '')
			return <div>{formatted}</div>
		},
	},
	{
		accessorKey: 'mensal',
		header: ({ column }) => {
			return (
				<Button
					size="sm"
					className="text-wrap"
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					COTA MENSAL
					<SortingIcon direction={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const amount = Number.parseFloat(row.getValue('mensal'))
			const formatted = new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			})
				.format(amount)
				.replace('R$', '')

			return <div>{formatted}</div>
		},
		footer: ({ table }) => {
			const total = table
				.getFilteredRowModel()
				.rows.reduce(
					(total, row) => total + Number.parseFloat(row.getValue('mensal')),
					0,
				)

			const formatted = new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			})
				.format(total)
				.replace('R$', '')

			return <div>{formatted}</div>
		},
	},
	{
		accessorKey: 'vendas',
		header: ({ column }) => {
			return (
				<Button
					className="text-wrap"
					size="sm"
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					VENDAS
					<SortingIcon direction={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const amount = Number.parseFloat(row.getValue('vendas'))
			const formatted = new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			})
				.format(amount)
				.replace('R$', '')

			return <div>{formatted}</div>
		},
		footer: ({ table }) => {
			const total = table
				.getFilteredRowModel()
				.rows.reduce(
					(total, row) => total + Number.parseFloat(row.getValue('vendas')),
					0,
				)

			const formatted = new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			})
				.format(total)
				.replace('R$', '')

			return <div>{formatted}</div>
		},
	},
	{
		accessorKey: 'desvio',
		header: ({ column }) => {
			return (
				<Button
					size="sm"
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					DESVIO
					<SortingIcon direction={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const amount = Number.parseFloat(row.getValue('desvio'))
			return <div>{amount}</div>
		},
	},
	{
		accessorKey: 'faturamento',
		header: ({ column }) => {
			return (
				<Button
					size="sm"
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					FATURAMENTO
					<SortingIcon direction={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const amount = Number.parseFloat(row.getValue('faturamento'))
			const formatted = new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			})
				.format(amount)
				.replace('R$', '')

			return <div>{formatted}</div>
		},
		footer: ({ table }) => {
			const total = table
				.getFilteredRowModel()
				.rows.reduce(
					(total, row) =>
						total + Number.parseFloat(row.getValue('faturamento')),
					0,
				)

			const formatted = new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL',
			})
				.format(total)
				.replace('R$', '')

			return <div>{formatted}</div>
		},
	},
	{
		accessorKey: 'participacao',
		header: ({ column }) => {
			return (
				<Button
					size="sm"
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				>
					PARTICIPAÇÃO
					<SortingIcon direction={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => {
			const amount = Number.parseFloat(row.getValue('participacao'))
			return <div>{amount}</div>
		},
	},
]

type SellerReportTableProps = {
	data: SellerReportType[]
}

export function SellersReportTable({ data }: SellerReportTableProps) {
	const [sorting, setSorting] = React.useState<SortingState>([])
	const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
		[],
	)
	const [columnVisibility, setColumnVisibility] =
		React.useState<VisibilityState>({})

	const table = useReactTable({
		data,
		columns,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
		},
	})

	return (
		<div className="w-full">
			<div className="rounded-md border mt-6">
				<Table>
					<TableHeader className="text-xs">
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id} className="-space-x-10">
								{headerGroup.headers.map((header) => {
									return (
										<TableHead key={header.id}>
											{header.isPlaceholder
												? null
												: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
										</TableHead>
									)
								})}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && 'selected'}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id}>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell
									colSpan={columns.length}
									className="h-24 text-center"
								>
									Nenhum resultado
								</TableCell>
							</TableRow>
						)}
					</TableBody>
					<TableFooter>
						{table.getFooterGroups().map((group) => (
							<TableRow key={group.id}>
								{group.headers.map((column) => (
									<TableCell key={column.id}>
										{column.isPlaceholder
											? null
											: flexRender(
												column.column.columnDef.footer,
												column.getContext(),
											)}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableFooter>
				</Table>
			</div>
		</div>
	)
}
